import { combineReducers } from 'redux';
import type { History } from 'history';
import { createRouterReducer } from '@lagunovsky/redux-react-router';
import { authReducer } from '@cbridge-packages/auth';
import { detailsSidebarReducer } from '@cbridge-packages/details-sidebar';
import { multiSelectSidebarReducer } from '@cbridge-packages/multi-select-sidebar';
import { infinityDataGridReducer } from '@cbridge-modules/infinity-data-grid';
import { notificationReducer } from '@cbridge-packages/notification';
import { symbolAssetsReducer } from '@cbridge-packages/symbol-assets';
import { symbolGroupsReducer } from '@cbridge-packages/symbol-groups';
import { symbolReducer } from '@cbridge-packages/symbol';
import { symbolSyntheticReducer } from '@cbridge-packages/symbol-synthetic';
import { symbolLpReducer } from '@cbridge-packages/symbol-lp';
import { markupReducer } from '@cbridge-packages/markup';
import { markupProfileReducer } from '@cbridge-packages/markup-profile';
import { scheduleReducer } from '@cbridge-packages/schedule';
import { scheduleIntervalReducer } from '@cbridge-packages/schedule-interval';
import { nodeReducer } from '@cbridge-packages/node';
import { streamReducer } from '@cbridge-packages/stream';
import { userListReducer } from '@cbridge-packages/user-list';
import { serverReducer } from '@cbridge-packages/server';
import { adjustmentProfileReducer } from '@cbridge-packages/adjustment-profile';
import { symbolPoolReducer } from '@cbridge-modules/symbol-pool-filter';
import { alertSubscriptionReducer } from '@cbridge-modules/alert-subscription';
import { priceFilteringReducer } from '@cbridge-packages/price-filtering-profile';
import { clientLpReducer } from '@cbridge-packages/lp-client';
import { pricingProfileReducer } from '@cbridge-packages/pricing-profile';
import { spreadProfileReducer } from '@cbridge-packages/spread-profile';
import { controllerSymbolLpReducer } from '@cbridge-modules/controller-symbol-lp';
import { controllerSymbolClientReducer } from '@cbridge-modules/controller-symbol-client';
import { searchFilterReducer } from '@cbridge-modules/search-filter';
import { serversFilterReducer } from '@cbridge-modules/servers-filter';
import { streamsFilterReducer } from '@cbridge-modules/streams-filter';
import { symbolMappingFilterReducer } from '@cbridge-modules/symbol-mapping-filter';
import { symbolClientReducer } from '@cbridge-packages/symbol-client';
import { orderLifetimeReducer } from '@cbridge-packages/order-lifetime';
import { rulesManagementReducer } from '@cbridge-modules/rules-management-page';
import { adjustmentRuleReducer } from '@cbridge-packages/adjustment-rules';
import { orderLifetimeRuleReducer } from '@cbridge-packages/order-lifetime-rules';
import { priceFilteringRuleReducer } from '@cbridge-packages/price-filtering-rules';
import { pricingRuleReducer } from '@cbridge-packages/pricing-rules';
import { routingRuleReducer } from '@cbridge-packages/routing-rules';
import { routingProfileReducer } from '@cbridge-packages/routing-profile';
import { dictSymbolReducer } from '@cbridge-packages/dict-symbols';
import { dictTimezoneReducer } from '@cbridge-packages/dict-timezones';
import { userSettingsReducer } from '@cbridge-packages/user-settings';
import { alertsDashboardReducer } from '@cbridge-modules/alerts-dashboard';
import { symbolPriceReducer } from '@cbridge-packages/symbol-price';
import { supersetReducer } from '@cbridge-packages/superset';
import { performanceMetricsReducer } from '@cbridge-packages/performance-metrics';
import { exposureReducer } from '@cbridge-packages/exposure';
import { exposureDashboardReducer } from '@cbridge-modules/exposure-dashboard';
import { markoutReducer } from '@cbridge-packages/markout';
import { coverageServerReducer } from '@cbridge-packages/coverage-server';
import { coverageReportReducer } from '@cbridge-packages/coverage-report';
import { coverageAccountReducer } from '@cbridge-packages/coverage-account';
import { coverageRuleReducer } from '@cbridge-packages/coverage-rules';
import { symbolQuotesReducer } from '@cbridge-packages/symbol-quotes';

export const createRootReducer = (history: History) => combineReducers({
  auth: authReducer,
  router: createRouterReducer(history),
  infinityDataGrid: infinityDataGridReducer,
  detailsSidebar: detailsSidebarReducer,
  multiSelectSidebar: multiSelectSidebarReducer,
  notification: notificationReducer,
  symbolAssets: symbolAssetsReducer,
  symbolGroups: symbolGroupsReducer,
  symbol: symbolReducer,
  symbolSynthetic: symbolSyntheticReducer,
  symbolLp: symbolLpReducer,
  controllerSymbolLp: controllerSymbolLpReducer,
  markup: markupReducer,
  markupProfile: markupProfileReducer,
  schedule: scheduleReducer,
  scheduleInterval: scheduleIntervalReducer,
  orderLifetime: orderLifetimeReducer,
  adjustmentProfile: adjustmentProfileReducer,
  node: nodeReducer,
  stream: streamReducer,
  userList: userListReducer,
  clientLp: clientLpReducer,
  priceFiltering: priceFilteringReducer,
  server: serverReducer,
  symbolPool: symbolPoolReducer,
  alertSubscription: alertSubscriptionReducer,
  pricingProfile: pricingProfileReducer,
  spreadProfile: spreadProfileReducer,
  searchFilter: searchFilterReducer,
  serversFilter: serversFilterReducer,
  streamsFilter: streamsFilterReducer,
  symbolMappingFilter: symbolMappingFilterReducer,
  symbolClient: symbolClientReducer,
  controllerSymbolClient: controllerSymbolClientReducer,
  rulesManagement: rulesManagementReducer,
  adjustmentRule: adjustmentRuleReducer,
  orderLifetimeRule: orderLifetimeRuleReducer,
  priceFilteringRule: priceFilteringRuleReducer,
  pricingRule: pricingRuleReducer,
  routingRule: routingRuleReducer,
  routingProfile: routingProfileReducer,
  dictSymbol: dictSymbolReducer,
  dictTimezone: dictTimezoneReducer,
  userSettings: userSettingsReducer,
  alertsDashboard: alertsDashboardReducer,
  symbolPrice: symbolPriceReducer,
  superset: supersetReducer,
  performanceMetrics: performanceMetricsReducer,
  exposure: exposureReducer,
  exposureDashboard: exposureDashboardReducer,
  markout: markoutReducer,
  coverageServer: coverageServerReducer,
  coverageReport: coverageReportReducer,
  coverageAccount: coverageAccountReducer,
  coverageRule: coverageRuleReducer,
  symbolQuotes: symbolQuotesReducer,
});
